import { type ComputedRef, type Ref, computed, ref } from 'vue'
import { ChatApiService, type ChatMemberResponse } from '../services/chatApiService'
import {
  fetchMatrixRoomMembers,
  isSyntheticMatrixChatId,
  resolveMatrixRoomIdByChatId,
} from '../services/matrixChatClient'
import { subscribeMatrixRoomMembership } from '../services/matrixRoomMembersService'
import {
  getCachedMatrixPresence,
  subscribeMatrixPresence,
  trackMatrixPresence,
} from '../services/matrixPresenceService'

interface UseChatMembersOptions {
  chatId: Ref<number | string | undefined> | ComputedRef<number | string | undefined>
  matrixRoomId?: Ref<string | undefined> | ComputedRef<string | undefined>
  pageSize?: number
  onError?: (error: unknown) => void
}

export function useChatMembers(options: UseChatMembersOptions) {
  const { chatId, matrixRoomId, pageSize = 100, onError } = options

  const members = ref<ChatMemberResponse[]>([])
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const nextCursor = ref<string | null>(null)
  const totalCount = ref<number | null>(null)
  const resolvedMatrixRoomId = ref<string | undefined>()
  const usesBackendMembers = ref(false)

  const isMatrixChat = computed(() => !usesBackendMembers.value && Boolean(resolvedMatrixRoomId.value))
  const hasMore = computed(() => usesBackendMembers.value && nextCursor.value !== null)

  let unsubscribeMembership: (() => void) | undefined
  let unsubscribePresence: (() => void) | undefined
  let subscribedRoomId: string | undefined

  function stopPresenceUpdates() {
    unsubscribePresence?.()
    unsubscribePresence = undefined
  }

  function stopLiveUpdates() {
    unsubscribeMembership?.()
    unsubscribeMembership = undefined
    subscribedRoomId = undefined
    stopPresenceUpdates()
  }

  /** Админы в начале списка */
  function sortAdminsFirst(list: ChatMemberResponse[]): ChatMemberResponse[] {
    return [...list].sort((a, b) => Number(b.is_admin) - Number(a.is_admin))
  }

  function applyPresenceToMembers(list: ChatMemberResponse[]): ChatMemberResponse[] {
    return list.map(member => {
      const mxid = member.user_uuid?.trim()
      if (!mxid?.startsWith('@')) {
        return member
      }

      const online = getCachedMatrixPresence(mxid)
      if (online === undefined || member.online === online) {
        return member
      }

      return { ...member, online }
    })
  }

  async function enrichMembersPresence(list: ChatMemberResponse[]): Promise<ChatMemberResponse[]> {
    const mxids = list
      .map(member => member.user_uuid?.trim())
      .filter((mxid): mxid is string => Boolean(mxid?.startsWith('@')))

    if (!mxids.length) {
      return list
    }

    await trackMatrixPresence(mxids)
    return applyPresenceToMembers(list)
  }

  function startPresenceUpdates() {
    stopPresenceUpdates()
    unsubscribePresence = subscribeMatrixPresence((mxid, online) => {
      const index = members.value.findIndex(member => member.user_uuid === mxid)
      if (index === -1 || members.value[index].online === online) {
        return
      }

      const next = [...members.value]
      next[index] = { ...next[index], online }
      members.value = next
    })
  }

  function startLiveUpdates(roomId: string) {
    if (subscribedRoomId === roomId) {
      return
    }

    stopLiveUpdates()
    subscribedRoomId = roomId
    unsubscribeMembership = subscribeMatrixRoomMembership(roomId, () => {
      void loadMatrixMembers(true, { silent: true })
    })
    startPresenceUpdates()
  }

  async function resolveRoomId(): Promise<string | undefined> {
    const directRoomId = matrixRoomId?.value
    if (directRoomId) {
      return directRoomId
    }

    const id = chatId.value
    if (typeof id === 'number') {
      return resolveMatrixRoomIdByChatId(id)
    }

    return undefined
  }

  function shouldLoadFromBackend(id: number | string | undefined): id is number {
    return typeof id === 'number' && !isSyntheticMatrixChatId(id)
  }

  async function loadBackendMembers(
    reset = true,
    options: { silent?: boolean } = {},
  ) {
    const { silent = false } = options
    const id = chatId.value
    if (!shouldLoadFromBackend(id)) {
      return
    }

    usesBackendMembers.value = true
    stopLiveUpdates()

    if (reset && !silent) {
      isLoading.value = true
      members.value = []
      nextCursor.value = null
      totalCount.value = null
    } else if (!reset) {
      isLoadingMore.value = true
    }

    try {
      const page = await ChatApiService.fetchMembers(id, {
        cursor: reset ? undefined : nextCursor.value ?? undefined,
        pageSize,
      })

      const nextMembers = sortAdminsFirst(
        reset ? page.members : [...members.value, ...page.members],
      )
      // Сразу рисуем список; presence — фоном, без блокировки paint
      members.value = nextMembers
      nextCursor.value = page.nextCursor
      // Без count не угадываем по длине страницы; если страниц больше нет — длина = total.
      totalCount.value = page.count ?? (page.nextCursor ? null : members.value.length)
      startPresenceUpdates()
      void enrichMembersPresence(nextMembers).then(enriched => {
        members.value = sortAdminsFirst(enriched)
      })
    } catch (error) {
      onError?.(error)
      if (reset && !silent) {
        members.value = []
      }
    } finally {
      if (!silent) {
        isLoading.value = false
      }
      isLoadingMore.value = false
    }
  }

  async function loadMatrixMembers(
    reset = true,
    options: { silent?: boolean } = {},
  ) {
    const { silent = false } = options
    const roomId = await resolveRoomId()
    resolvedMatrixRoomId.value = roomId
    usesBackendMembers.value = false

    if (!roomId) {
      stopLiveUpdates()
      return
    }

    if (reset && !silent) {
      isLoading.value = true
      members.value = []
      nextCursor.value = null
      totalCount.value = null
    }

    try {
      const loadedMembers = sortAdminsFirst(
        await fetchMatrixRoomMembers(roomId, { mode: 'light' }),
      )
      members.value = loadedMembers
      totalCount.value = loadedMembers.length
      nextCursor.value = null
      startLiveUpdates(roomId)
      void enrichMembersPresence(loadedMembers).then(enriched => {
        members.value = sortAdminsFirst(enriched)
      })
    } catch (error) {
      onError?.(error)
      if (reset && !silent) {
        members.value = []
      }
    } finally {
      if (!silent) {
        isLoading.value = false
      }
      isLoadingMore.value = false
    }
  }

  async function load(reset = true) {
    const id = chatId.value
    if (shouldLoadFromBackend(id)) {
      resolvedMatrixRoomId.value = matrixRoomId?.value
        || await resolveMatrixRoomIdByChatId(id)
      await loadBackendMembers(reset)
      if (members.value.length || !resolvedMatrixRoomId.value) {
        return
      }
      // Backend пустой — fallback на Matrix members
      await loadMatrixMembers(reset)
      return
    }

    await loadMatrixMembers(reset)
  }

  function handleScroll(event: Event) {
    if (!usesBackendMembers.value) {
      return
    }

    const target = event.target as HTMLElement
    if (!target || isLoadingMore.value || !hasMore.value) {
      return
    }

    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight
    if (scrollBottom < 100) {
      void loadBackendMembers(false)
    }
  }

  async function fetchAll(): Promise<ChatMemberResponse[]> {
    const id = chatId.value
    if (shouldLoadFromBackend(id)) {
      if (!members.value.length || nextCursor.value) {
        await loadBackendMembers(true)
        while (nextCursor.value) {
          await loadBackendMembers(false, { silent: true })
        }
      }

      return [...members.value]
    }

    const roomId = resolvedMatrixRoomId.value || await resolveRoomId()
    if (roomId) {
      if (!members.value.length) {
        await loadMatrixMembers(true)
      }

      return [...members.value]
    }

    return []
  }

  function reset() {
    stopLiveUpdates()
    members.value = []
    nextCursor.value = null
    totalCount.value = null
    resolvedMatrixRoomId.value = undefined
    usesBackendMembers.value = false
    isLoading.value = false
    isLoadingMore.value = false
  }

  return {
    members,
    isLoading,
    isLoadingMore,
    nextCursor,
    totalCount,
    hasMore,
    resolvedMatrixRoomId,
    load,
    handleScroll,
    fetchAll,
    reset,
  }
}
